import { FC } from 'react';
import { Button, CircularProgress, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import { LeftSide, SettingBox, StyledForm, Subtitle, WhiteWrapper } from '@/pages/Settings/components/styles';
import { SmallerChip } from '@/components/Tag/styles';
import ActionDialog from '@/components/ActionDialog';
import { useSmartTags } from '../hooks/useSmartTags';
import MergeSuggestionCard from './MergeSuggestionCard';

const SmartTags: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    tags,
    tagCounts,
    isLoading,
    suggestions,
    isLoadingSuggestions,
    hasFetchedSuggestions,
    isMerging,
    mergingIndex,
    fetchSuggestions,
    acceptSuggestion,
    dismissSuggestion,
    openDeleteDialog,
    selectedTag,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog,
    handleDeleteTag,
    openEditDialog,
    newTagName,
    setNewTagName,
    handleOpenEditDialog,
    handleCloseEditDialog,
    handleEditTag,
  } = useSmartTags();

  return (
    <SettingBox>
      <LeftSide>
        <Typography variant="body1" fontWeight="700">
          Smart Tags
        </Typography>
        <Subtitle variant="body2">Use AI to analyze and merge similar tags for better organization.</Subtitle>
      </LeftSide>
      <StyledForm>
        <Stack>
          <WhiteWrapper>
            {/* Tag cloud */}
            {tags.length === 0 ? (
              <Typography variant="body2">You don&apos;t have any tags yet.</Typography>
            ) : (
              <Stack direction="row" flexWrap="wrap" gap={isMobile ? 0.5 : 1} mb={2}>
                {tags
                  .filter((tag) => tag && typeof tag === 'string')
                  .map((tag) => (
                    <SmallerChip
                      key={tag}
                      label={`${tag} (${tagCounts[tag] ?? 0})`}
                      onDelete={() => handleOpenDeleteDialog(tag)}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      onClick={() => handleOpenEditDialog(tag)}
                      sx={{
                        height: isMobile ? '28px' : '24px',
                        margin: isMobile ? '2px' : 0,
                        '& .MuiChip-label': {
                          fontSize: isMobile ? '11px' : '12px',
                          padding: isMobile ? '4px 6px' : '4px 8px',
                        },
                        '& .MuiChip-deleteIcon': {
                          color: 'inherit',
                          fontSize: isMobile ? '14px' : '16px',
                        },
                      }}
                    />
                  ))}
              </Stack>
            )}

            {/* Actions section */}
            <Stack spacing={1} mt={2}>
              <Typography variant="body2" fontWeight="700">
                Actions
              </Typography>
              {tags.length > 1 && (
                <Button
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={fetchSuggestions}
                  disabled={isLoadingSuggestions || isLoading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                >
                  Group with AI
                </Button>
              )}
            </Stack>

            {/* Loading state */}
            {isLoadingSuggestions && (
              <Stack alignItems="center" mt={2} spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Analyzing your tags with AI...
                </Typography>
              </Stack>
            )}

            {/* Suggestion cards */}
            {suggestions.length > 0 && (
              <Stack spacing={2} mt={2}>
                {suggestions.map((suggestion, index) => (
                  <MergeSuggestionCard
                    key={`${suggestion.target_tag}-${index}`}
                    suggestion={suggestion}
                    tagCounts={tagCounts}
                    onAccept={() => acceptSuggestion(suggestion, index)}
                    onDismiss={() => dismissSuggestion(index)}
                    isMerging={isMerging && mergingIndex === index}
                    isMobile={isMobile}
                  />
                ))}
              </Stack>
            )}

            {/* Empty result message */}
            {hasFetchedSuggestions && suggestions.length === 0 && !isLoadingSuggestions && (
              <Stack alignItems="center" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  No grouping suggestions found. Your tags look well-organized!
                </Typography>
              </Stack>
            )}

            {/* Loading indicator for tag operations */}
            {isLoading && (
              <Stack alignItems="center" mt={2} spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="body2" color="text.secondary">
                  Updating tags across all your notes...
                </Typography>
              </Stack>
            )}
          </WhiteWrapper>
        </Stack>
      </StyledForm>

      {/* Delete Tag Dialog */}
      <ActionDialog
        open={openDeleteDialog}
        title="Delete Tag"
        content={`Are you sure you want to delete the tag "${selectedTag}"? This will remove the tag from all your notes.`}
        variant="danger"
        confirmText="Delete"
        onConfirm={handleDeleteTag}
        onCancel={handleCloseDeleteDialog}
        loading={isLoading}
        ariaLabelledBy="delete-dialog-title"
        ariaDescribedBy="delete-dialog-description"
      />

      {/* Edit Tag Dialog */}
      <ActionDialog
        open={openEditDialog}
        title="Edit Tag"
        content={`Edit the tag "${selectedTag}". This will update the tag across all your notes.`}
        variant="form"
        confirmText="Save"
        onConfirm={handleEditTag}
        onCancel={handleCloseEditDialog}
        loading={isLoading}
        ariaLabelledBy="edit-dialog-title"
        ariaDescribedBy="edit-dialog-description"
      >
        <TextField
          autoFocus
          margin="dense"
          label="New Tag Name"
          type="text"
          fullWidth
          variant="outlined"
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          disabled={isLoading}
        />
      </ActionDialog>
    </SettingBox>
  );
};

export default SmartTags;
